import React, { useState } from 'react';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  CheckSquare, Plus, Search, Clock, AlertTriangle, CheckCircle2,
  Circle, ChevronDown, ChevronLeft, ChevronRight, Calendar, Star, LayoutGrid, 
  List, UserCheck, Columns3, MessageSquare, Target, Zap, Edit2, Trash2
} from 'lucide-react';

const MOCK_TASKS = [
  { id: 1, title: 'Follow up with FIRA team on campaign results', priority: 'high', status: 'in_progress', due_date: '2026-05-08', project: 'Campaign Q2', type: 'follow-up', subtasks: [{id:'s1',text:'Check FIRA deposit numbers',done:true},{id:'s2',text:'Send report to management',done:false}], comments: [{user:'Winda',text:'Data dah ready',time:'2h ago'}] },
  { id: 2, title: 'Prepare weekly report for management', priority: 'high', status: 'pending', due_date: '2026-05-08', project: '', type: 'execution', subtasks: [], comments: [] },
  { id: 3, title: 'Review FGC creative materials before launch', priority: 'medium', status: 'pending', due_date: '2026-05-09', project: 'Campaign Q2', type: 'review', subtasks: [{id:'s3',text:'Review poster designs',done:false},{id:'s4',text:'Check video quality',done:false}], comments: [] },
  { id: 4, title: 'Update MBE broker config for May', priority: 'low', status: 'completed', due_date: '2026-05-07', project: '', type: 'execution', subtasks: [], comments: [] },
  { id: 5, title: 'Check CIKO SS signal win rate decline', priority: 'high', status: 'pending', due_date: '2026-05-08', project: '', type: 'follow-up', subtasks: [], comments: [] },
  { id: 6, title: 'Plan June webinar campaign blueprint', priority: 'medium', status: 'pending', due_date: '2026-05-15', project: 'Campaign Q2', type: 'planning', subtasks: [{id:'s5',text:'Draft outline',done:true},{id:'s6',text:'Set budget',done:false},{id:'s7',text:'Confirm speakers',done:false}], comments: [{user:'Munir',text:'Target 200 signups',time:'1d ago'}] },
  { id: 7, title: 'Coordinate with Winda on new ad creatives', priority: 'medium', status: 'in_progress', due_date: '2026-05-10', project: '', type: 'execution', subtasks: [], comments: [] },
  { id: 8, title: 'Audit Telegram channel content for all IBs', priority: 'low', status: 'pending', due_date: '2026-05-12', project: '', type: 'review', subtasks: [], comments: [] },
];

const priorityDot = { high: 'bg-red-400', medium: 'bg-amber-400', low: 'bg-emerald-400' };
const priorityLabel = { high: 'High', medium: 'Medium', low: 'Low' };
const statusIcon = { pending: Circle, in_progress: Clock, completed: CheckCircle2 };
const statusColor = { pending: 'text-amber-400', in_progress: 'text-blue-400', completed: 'text-emerald-400' };
const statusBg = { pending: 'bg-amber-500/10 border-amber-500/20', in_progress: 'bg-blue-500/10 border-blue-500/20', completed: 'bg-emerald-500/10 border-emerald-500/20' };

export default function MyTasksV2Page() {
  const [filter, setFilter] = useState('all');
  const [viewTab, setViewTab] = useState('tasks');
  const [listMode, setListMode] = useState('list');
  const [expanded, setExpanded] = useState(null);
  const [projectFilter, setProjectFilter] = useState('all');

  const today = '2026-05-08';
  const overdue = MOCK_TASKS.filter(t => t.status !== 'completed' && t.due_date < today);
  const dueToday = MOCK_TASKS.filter(t => t.status !== 'completed' && t.due_date === today);

  const filtered = (() => {
    let f = filter === 'all' ? MOCK_TASKS.filter(t => t.status !== 'completed')
      : filter === 'completed' ? MOCK_TASKS.filter(t => t.status === 'completed')
      : MOCK_TASKS.filter(t => t.status === filter);
    if (projectFilter !== 'all') f = f.filter(t => t.project === projectFilter);
    return f;
  })();

  const stats = {
    active: MOCK_TASKS.filter(t => t.status !== 'completed').length,
    overdue: overdue.length,
    pending: MOCK_TASKS.filter(t => t.status === 'pending').length,
    inProgress: MOCK_TASKS.filter(t => t.status === 'in_progress').length,
    completed: MOCK_TASKS.filter(t => t.status === 'completed').length,
  };

  const TaskCard = ({ task }) => {
    const SI = statusIcon[task.status];
    const isExp = expanded === task.id;
    const subsDone = task.subtasks.filter(s => s.done).length;

    return (
      <div className={`rounded-xl border transition-all ${task.status === 'completed' ? 'opacity-40 border-[hsl(var(--border))]' : isExp ? 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/3' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'} bg-[hsl(var(--card))]`}>
        {/* Main Row */}
        <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(isExp ? null : task.id)}>
          <button className={`shrink-0 ${statusColor[task.status]} hover:scale-110 transition-transform`}>
            <SI size={18} />
          </button>
          <div className={`w-2 h-2 rounded-full shrink-0 ${priorityDot[task.priority]}`} title={priorityLabel[task.priority]} />
          <div className="flex-1 min-w-0">
            <p className={`text-[13px] font-medium leading-tight ${task.status === 'completed' ? 'line-through text-[hsl(var(--text-muted))]' : 'text-[hsl(var(--text-primary))]'}`}>{task.title}</p>
            <div className="flex items-center gap-2 mt-1">
              {task.project && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-medium">{task.project}</span>}
              {task.due_date && (
                <span className={`text-[9px] flex items-center gap-0.5 ${task.due_date < today && task.status !== 'completed' ? 'text-red-400 font-semibold' : task.due_date === today ? 'text-amber-400' : 'text-[hsl(var(--text-muted))]'}`}>
                  <Calendar size={8} /> {task.due_date === today ? 'Today' : task.due_date < today ? 'Overdue' : task.due_date.slice(5)}
                </span>
              )}
              {task.subtasks.length > 0 && (
                <span className="text-[9px] text-[hsl(var(--text-muted))] flex items-center gap-0.5">
                  <CheckSquare size={8} /> {subsDone}/{task.subtasks.length}
                </span>
              )}
              {task.comments.length > 0 && (
                <span className="text-[9px] text-[hsl(var(--text-muted))] flex items-center gap-0.5">
                  <MessageSquare size={8} /> {task.comments.length}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 shrink-0">
            <button className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] transition-all"><Star size={13} /></button>
            <button className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-white transition-all"><Edit2 size={13} /></button>
          </div>
          <ChevronDown size={14} className={`text-[hsl(var(--text-muted))] transition-transform shrink-0 ${isExp ? 'rotate-180' : ''}`} />
        </div>

        {/* Expanded Details */}
        {isExp && (
          <div className="px-4 pb-4 pt-1 border-t border-[hsl(var(--border))]/30 space-y-3">
            {/* Subtasks */}
            {task.subtasks.length > 0 && (
              <div>
                <p className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] font-bold mb-1.5">Subtasks</p>
                <div className="space-y-1">
                  {task.subtasks.map(st => (
                    <div key={st.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--surface-elevated))] transition-all">
                      {st.done ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> : <Circle size={14} className="text-[hsl(var(--text-muted))] shrink-0" />}
                      <span className={`text-xs ${st.done ? 'line-through text-[hsl(var(--text-muted))]' : 'text-white'}`}>{st.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Comments */}
            {task.comments.length > 0 && (
              <div>
                <p className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] font-bold mb-1.5">Comments</p>
                {task.comments.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1.5">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center text-white text-[7px] font-bold shrink-0">{c.user.charAt(0)}</div>
                    <div>
                      <span className="text-[10px] font-medium text-white">{c.user}</span>
                      <span className="text-[9px] text-[hsl(var(--text-muted))] ml-1.5">{c.time}</span>
                      <p className="text-[11px] text-[hsl(var(--text-secondary))]">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Actions */}
            <div className="flex items-center gap-2 pt-1">
              <Badge className={`text-[8px] border ${statusBg[task.status]} ${statusColor[task.status]}`}>{task.status.replace('_', ' ')}</Badge>
              <Badge className="text-[8px] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))]">{priorityLabel[task.priority]}</Badge>
            </div>
          </div>
        )}
      </div>
    );
  };

  const GridCard = ({ task }) => {
    const SI = statusIcon[task.status];
    const subsDone = task.subtasks.filter(s => s.done).length;
    return (
      <div className={`rounded-xl border p-4 transition-all ${task.status === 'completed' ? 'opacity-40' : 'hover:border-[hsl(var(--border-hover))]'} border-[hsl(var(--border))] bg-[hsl(var(--card))]`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <SI size={14} className={statusColor[task.status]} />
            <div className={`w-2 h-2 rounded-full ${priorityDot[task.priority]}`} />
          </div>
          {task.due_date && (
            <span className={`text-[9px] ${task.due_date < today ? 'text-red-400 font-bold' : task.due_date === today ? 'text-amber-400' : 'text-[hsl(var(--text-muted))]'}`}>
              {task.due_date === today ? 'Today' : task.due_date < today ? 'Overdue' : task.due_date.slice(5)}
            </span>
          )}
        </div>
        <p className={`text-[12px] font-medium leading-snug mb-2 ${task.status === 'completed' ? 'line-through text-[hsl(var(--text-muted))]' : 'text-white'}`}>{task.title}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {task.project && <span className="text-[8px] px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">{task.project}</span>}
          {task.subtasks.length > 0 && <span className="text-[8px] text-[hsl(var(--text-muted))]"><CheckSquare size={8} className="inline" /> {subsDone}/{task.subtasks.length}</span>}
          {task.comments.length > 0 && <span className="text-[8px] text-[hsl(var(--text-muted))]"><MessageSquare size={8} className="inline" /> {task.comments.length}</span>}
        </div>
      </div>
    );
  };

  // Kanban columns
  const kanbanCols = [
    { key: 'pending', label: 'Pending', color: '#f59e0b', tasks: MOCK_TASKS.filter(t => t.status === 'pending') },
    { key: 'in_progress', label: 'In Progress', color: '#3b82f6', tasks: MOCK_TASKS.filter(t => t.status === 'in_progress') },
    { key: 'completed', label: 'Completed', color: '#10b981', tasks: MOCK_TASKS.filter(t => t.status === 'completed') },
  ];

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={CheckSquare} title="My Tasks (V2 Mockup)" subtitle="Redesigned layout — all features preserved"
          iconBg="bg-gradient-to-br from-[#E63946] to-[#F97316] shadow-[#E63946]/20"
          action={<div className="flex gap-2">
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">MOCKUP</Badge>
            <Button className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white text-xs h-9"><Plus size={14} className="mr-1.5" /> Add Task</Button>
          </div>}
        />

        {/* Today's Focus */}
        {(overdue.length > 0 || dueToday.length > 0) && (
          <div className="mt-4 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--primary))] mb-2.5 flex items-center gap-1.5">
              <AlertTriangle size={12} /> Today's Focus
            </h3>
            <div className="space-y-1.5">
              {overdue.map(t => (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/20">
                  <div className={`w-2 h-2 rounded-full ${priorityDot[t.priority]}`} />
                  <span className="text-xs text-white flex-1">{t.title}</span>
                  <span className="text-[9px] text-red-400 font-semibold">OVERDUE</span>
                </div>
              ))}
              {dueToday.map(t => (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <div className={`w-2 h-2 rounded-full ${priorityDot[t.priority]}`} />
                  <span className="text-xs text-white flex-1">{t.title}</span>
                  <span className="text-[9px] text-amber-400 font-semibold">DUE TODAY</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Month Selector Mock */}
        <div className="mt-4 flex items-center gap-2 mb-4">
          <button className="p-1 rounded text-[hsl(var(--text-muted))] hover:text-white"><ChevronLeft size={16} /></button>
          {['All 2026','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
            <button key={m} className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${m === 'May' ? 'bg-[hsl(var(--primary))] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}>{m}</button>
          ))}
          <button className="p-1 rounded text-[hsl(var(--text-muted))] hover:text-white"><ChevronRight size={16} /></button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Active', value: stats.active, vc: '' },
            { label: 'Overdue', value: stats.overdue, vc: 'text-red-400' },
            { label: 'Pending', value: stats.pending, vc: 'text-amber-400' },
            { label: 'In Progress', value: stats.inProgress, vc: 'text-blue-400' },
            { label: 'Completed', value: stats.completed, vc: 'text-emerald-400' },
          ].map(s => (
            <div key={s.label} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-3 text-center">
              <p className={`text-[9px] uppercase tracking-wider ${s.vc || 'text-[hsl(var(--text-muted))]'}`}>{s.label}</p>
              <p className={`text-xl font-bold ${s.vc}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs + Toolbar */}
        <div className="flex items-center justify-between mb-4 border-b border-[hsl(var(--border))] pb-3">
          <div className="flex gap-1">
            {[
              { key: 'tasks', label: 'Tasks', icon: CheckSquare },
              { key: 'kanban', label: 'Kanban', icon: Columns3 },
              { key: 'assigned', label: 'Assigned To Me', icon: UserCheck, badge: 3 },
              { key: 'calendar', label: 'Calendar', icon: Calendar },
            ].map(tab => (
              <Button key={tab.key} variant={viewTab === tab.key ? 'default' : 'ghost'} size="sm"
                onClick={() => setViewTab(tab.key)}
                className={`text-xs ${viewTab === tab.key ? 'bg-[hsl(var(--primary))] text-white' : 'text-[hsl(var(--text-secondary))]'}`}>
                <tab.icon size={14} className="mr-1.5" />{tab.label}
                {tab.badge && <span className="ml-1.5 text-[9px] bg-white/20 px-1.5 py-0.5 rounded-full">{tab.badge}</span>}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {viewTab === 'tasks' && (
              <div className="flex gap-1">
                {['all', 'pending', 'in_progress', 'completed'].map(f => (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${filter === f ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--card))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}>
                    {f === 'all' ? 'Active' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            )}
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <Input placeholder="Search..." className="pl-8 h-8 w-44 text-xs bg-[hsl(var(--card))]" />
            </div>
            {viewTab === 'tasks' && (
              <div className="flex bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-0.5">
                <button onClick={() => setListMode('list')} className={`p-1.5 rounded ${listMode === 'list' ? 'bg-[hsl(var(--primary))] text-white' : 'text-[hsl(var(--text-muted))]'}`}><List size={14} /></button>
                <button onClick={() => setListMode('grid')} className={`p-1.5 rounded ${listMode === 'grid' ? 'bg-[hsl(var(--primary))] text-white' : 'text-[hsl(var(--text-muted))]'}`}><LayoutGrid size={14} /></button>
              </div>
            )}
          </div>
        </div>

        {/* Project Folders */}
        {viewTab === 'tasks' && (
          <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
            <button onClick={() => setProjectFilter('all')}
              className={`shrink-0 px-3 py-2 rounded-lg border transition-all ${projectFilter === 'all' ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))]'}`}>
              <div className="text-[10px] font-bold text-white">All Tasks</div>
              <div className="text-[8px] text-[hsl(var(--text-muted))] mt-1">{MOCK_TASKS.filter(t => t.status !== 'completed').length} total</div>
            </button>
            {['Campaign Q2'].map(p => {
              const pTasks = MOCK_TASKS.filter(t => t.project === p);
              const pDone = pTasks.filter(t => t.status === 'completed').length;
              const pct = pTasks.length > 0 ? Math.round((pDone / pTasks.length) * 100) : 0;
              return (
                <button key={p} onClick={() => setProjectFilter(projectFilter === p ? 'all' : p)}
                  className={`shrink-0 px-3 py-2 rounded-lg border text-left transition-all ${projectFilter === p ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10' : 'border-[hsl(var(--border))] bg-[hsl(var(--card))]'}`}>
                  <div className="text-[10px] font-bold text-white">{p}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-16 h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                      <div className="h-full bg-[#10b981] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[9px] font-mono text-[#10b981]">{pct}%</span>
                  </div>
                  <div className="text-[8px] text-[hsl(var(--text-muted))] mt-0.5">{pDone}/{pTasks.length}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Quick Add */}
        {viewTab === 'tasks' && (
          <div className="mb-4">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/30 transition-all cursor-text">
              <Plus size={14} className="text-[hsl(var(--text-muted))]" />
              <span className="text-sm text-[hsl(var(--text-muted))]">Quick add task... press Enter</span>
            </div>
          </div>
        )}

        {/* Content */}
        {viewTab === 'tasks' && listMode === 'list' && (
          <div className="space-y-2">
            {filtered.map(task => <TaskCard key={task.id} task={task} />)}
          </div>
        )}

        {viewTab === 'tasks' && listMode === 'grid' && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(task => <GridCard key={task.id} task={task} />)}
          </div>
        )}

        {viewTab === 'kanban' && (
          <div className="grid grid-cols-3 gap-4">
            {kanbanCols.map(col => (
              <div key={col.key} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                <div className="p-3 border-b border-[hsl(var(--border))] flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-xs font-semibold text-white">{col.label}</span>
                  <span className="text-[9px] text-[hsl(var(--text-muted))] ml-auto">{col.tasks.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[200px]">
                  {col.tasks.map(t => (
                    <div key={t.id} className="p-3 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-all">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${priorityDot[t.priority]}`} />
                        {t.project && <span className="text-[8px] px-1 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">{t.project}</span>}
                      </div>
                      <p className="text-[11px] text-white leading-snug">{t.title}</p>
                      {t.due_date && <p className={`text-[8px] mt-1 ${t.due_date < today ? 'text-red-400' : 'text-[hsl(var(--text-muted))]'}`}><Calendar size={7} className="inline mr-0.5" />{t.due_date.slice(5)}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewTab === 'calendar' && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="text-center mb-4">
              <h3 className="text-sm font-bold text-white">May 2026</h3>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <div key={d} className="text-[9px] text-[hsl(var(--text-muted))] font-bold py-1">{d}</div>
              ))}
              {Array.from({length: 31}, (_, i) => i + 1).map(day => {
                const dateStr = `2026-05-${String(day).padStart(2,'0')}`;
                const dayTasks = MOCK_TASKS.filter(t => t.due_date === dateStr);
                return (
                  <div key={day} className={`p-1 rounded-lg min-h-[60px] border text-left ${day === 8 ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5' : 'border-[hsl(var(--border))]/30'}`}>
                    <span className={`text-[10px] ${day === 8 ? 'text-[hsl(var(--primary))] font-bold' : 'text-[hsl(var(--text-muted))]'}`}>{day}</span>
                    {dayTasks.map(t => (
                      <div key={t.id} className={`mt-0.5 px-1 py-0.5 rounded text-[7px] truncate ${t.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}>{t.title.slice(0, 20)}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewTab === 'assigned' && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 text-center">
            <UserCheck size={32} className="mx-auto mb-2 text-[hsl(var(--text-muted))]/20" />
            <p className="text-sm text-[hsl(var(--text-muted))]">Assigned To Me (Mockup)</p>
            <p className="text-xs text-[hsl(var(--text-muted))]/60 mt-1">Creative requests assigned to you will appear here</p>
          </div>
        )}

        {/* Design Notes */}
        <div className="mt-6 p-4 rounded-xl bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))]">
          <h4 className="text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase mb-2">V2 Design Changes</h4>
          <ul className="text-[11px] text-[hsl(var(--text-muted))] space-y-1">
            <li>• <strong>Today's Focus</strong> — Overdue + due today auto-highlighted at top</li>
            <li>• <strong>Cleaner task cards</strong> — Click to expand subtasks, comments. Compact by default</li>
            <li>• <strong>Grid view</strong> — Cards in 2-3 columns</li>
            <li>• <strong>Kanban</strong> — 3-column drag-style layout</li>
            <li>• <strong>Calendar</strong> — Month grid with tasks on dates</li>
            <li>• <strong>All features preserved</strong> — Tabs, search, filters, list/grid toggle, quick add, subtasks, comments</li>
          </ul>
        </div>
      </div>
    </ResponsiveLayout>
  );
}
